/* eslint-disable @typescript-eslint/no-explicit-any */
import { DataGridConfig } from 'components/DataGrid';
import { useAtom, useAtomValue } from 'jotai';
import { formatDate } from 'lib/helper';
import { Fragment, useMemo } from 'react';

import { selectedReportTypesAtom, reportTypesAtom } from './state';

// Helper to abbreviate full weekday names in schedule strings
const abbreviateSchedule = (schedule?: string) => {
  if (!schedule) return '';
  const map: Record<string, string> = {
    Monday: 'Mon',
    Tuesday: 'Tue',
    Wednesday: 'Wed',
    Thursday: 'Thu',
    Friday: 'Fri',
    Saturday: 'Sat',
    Sunday: 'Sun',
  };
  return schedule
    .split(',')
    .map((s) => s.trim())
    .map((s) => map[s] ?? s)
    .join(', ');
};

// Default column keys
const DEFAULT_COLUMN_KEYS_INDIVIDUAL = [
  'name',
  'schedule',
  'subscribed_by',
  'updated_at',
  'last_notified_at',
  'subscribed',
];

const DEFAULT_COLUMN_KEYS_BULK = ['name', 'schedule'];

/**
 * Custom hook for configuring the Subscription Modal DataGrid
 * Handles data manage bulk subscriptions
 * @param subscriptions Current user subscriptions
 * @param isBulk Whether this is for bulk subscription management
 * @returns Configuration object for the DataGrid component
 */
export const useSubscriptionModalDataConfig = (
  subscriptions: any,
  isBulk: boolean = false,
) => {
  const [selectedReportTypes, setSelectedReportTypes] = useAtom(
    selectedReportTypesAtom,
  );
  const reportTypes = useAtomValue(reportTypesAtom);

  const listingData = useMemo(() => {
    if (!reportTypes || reportTypes.length === 0) {
      return [];
    }

    // Create a map of all report types from API response
    const allReportTypesMap = reportTypes.reduce(
      (acc, reportType) => {
        if (!reportType || !reportType.id) return acc;

        const reportTypeKey = reportType.name.toLowerCase().replace(/ /g, '_');
        acc[reportTypeKey] = {
          id: reportType.id,
          key: reportTypeKey,
          name: reportType.name,
          subscribed: false,
          schedule: reportType.schedule,
          last_notified_at: 'Never',
          updated_at: null,
          created_by: null,
          subscribed_by: null,
          description: reportType.description,
          icon: reportType.icon || 'fa-file-alt',
        };
        return acc;
      },
      {} as Record<string, any>,
    );

    // If there are subscriptions, merge them with the report types map
    if (subscriptions && Object.keys(subscriptions).length > 0) {
      Object.entries(subscriptions).forEach(([key, value]: [string, any]) => {
        // If this subscription exists in our map, update it
        if (allReportTypesMap[key]) {
          allReportTypesMap[key] = {
            ...allReportTypesMap[key],
            subscribed: value?.subscribed || false,
            last_notified_at: value?.last_notified_at
              ? formatDate(new Date(value.last_notified_at))
              : 'Never',
            updated_at: value?.updated_at
              ? formatDate(new Date(value.updated_at))
              : null,
            subscribed_by: value?.subscribed_by || null,
            report_type_id: value?.report_type_id,
          };
        } else {
          // If it's a subscription not in our map (rare case), add it
          allReportTypesMap[key] = {
            id: key,
            name: value?.report_type?.name || key,
            subscribed: value?.subscribed || false,
            schedule: value?.schedule || 'Based on Request',
            last_notified_at: value?.last_notified_at
              ? formatDate(new Date(value.last_notified_at))
              : 'Never',
            updated_at: value?.updated_at
              ? formatDate(new Date(value.updated_at))
              : null,
            created_by: value?.created_by || null,
            subscribed_by: value?.subscribed_by || null,
            description:
              value?.report_type?.description || 'No description available',
            icon: value?.report_type?.icon || 'fa-file-alt',
          };
        }
      });
    }
    return Object.values(allReportTypesMap);
  }, [subscriptions, reportTypes]);

  // Get columns display configuration based on mode (Single or Bulk)
  const getColumnsDisplayConfig = () => {
    if (isBulk) {
      return {
        enabledColumnKeys: DEFAULT_COLUMN_KEYS_BULK,
        defaultColumnKeys: DEFAULT_COLUMN_KEYS_BULK,
        keysToBeExcluded: [
          'subscribed_by',
          'updated_at',
          'last_notified_at',
          'subscribed',
        ],
      };
    }

    return {
      enabledColumnKeys: DEFAULT_COLUMN_KEYS_INDIVIDUAL,
      defaultColumnKeys: DEFAULT_COLUMN_KEYS_INDIVIDUAL,
      keysToBeExcluded: [],
    };
  };

  const config: DataGridConfig = useMemo(
    () => ({
      rowIdKey: 'id',
      columns: [
        {
          key: 'name',
          label: 'Report Type',
          sortable: false,
          headerTooltip: 'Report type',
          headerCellStyle: {
            textAlign: 'left',
            maxWidth: 'auto',
            minWidth: '150px !important',
          },
          rowCellStyle: {
            textAlign: 'left',
            minWidth: '150px !important',
            maxWidth: 'auto',
          },
          maxCharLength: 100,
          overrideContents: (rowData) => {
            return (
              <div className="d-flex align-items-center">
                <i className={`fa ${rowData.icon} mr-2`}></i>
                <div>
                  <span className={`font-weight-bold`}>{rowData.name}</span>
                </div>
              </div>
            );
          },
          tooltip: (rowData) => {
            return (
              <Fragment>
                <strong className="mb-2">
                  <i className={`fa ${rowData.icon} mr-2`}></i>
                  {rowData.name}
                </strong>
                <br />
                {rowData.description}
              </Fragment>
            );
          },
        },
        {
          key: 'schedule',
          label: 'Schedule',
          sortable: false,
          headerTooltip: 'Schedule',
          overrideContents: (rowData) => (
            <span>{abbreviateSchedule(rowData.schedule)}</span>
          ),
        },
        {
          key: 'subscribed_by',
          label: 'Last Updated By',
          sortable: false,
          headerTooltip: 'Last updated by',
        },
        {
          key: 'updated_at',
          label: 'Updated At',
          sortable: false,
          headerTooltip: 'Updated at',
        },
        {
          key: 'last_notified_at',
          label: 'Last Notified',
          sortable: false,
          headerTooltip: 'Last notified',
        },
        {
          key: 'subscribed',
          label: 'Subscribed',
          sortable: false,
          headerTooltip: 'Subscription status',
        },
      ],
      columnsDisplayConfig: getColumnsDisplayConfig(),
      rowSelectionConfig: {
        preSelectedRows: selectedReportTypes,
        onSelectionChange: (updatedRowIds) => {
          setSelectedReportTypes(updatedRowIds);
        },
      },
      noDataMessage: 'No subscription data available',
    }),
    [selectedReportTypes, setSelectedReportTypes, isBulk],
  );

  return { config, data: listingData };
};
